import { AlertTriangle, ShieldCheck } from 'lucide-react';
import type React from 'react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { addSupplierPaymentMethod, type PaymentMethodOnFile } from '@/api/suppliers';
import { formatDate } from '@/lib/utils';
import {
  getBlockedReplacementType,
  getReplacementValidationError,
  type PaymentMethodType,
} from '@/utils/returned-payment-method';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

interface UpdatePaymentMethodModalProps {
  open: boolean;
  onClose: () => void;
  supplierId: string;
  supplierName: string;
  /** The returned method being replaced (shown for verification). */
  returnedMethod: PaymentMethodOnFile | null;
  /** Only client users may save a replacement method (matches approval gating). */
  canApprove: boolean;
  /** Called with the refreshed supplier detail after a successful save. */
  onSuccess: (updated: Awaited<ReturnType<typeof addSupplierPaymentMethod>>) => void;
}

export const UpdatePaymentMethodModal: React.FC<UpdatePaymentMethodModalProps> = ({
  open,
  onClose,
  supplierId,
  supplierName,
  returnedMethod,
  canApprove,
  onSuccess,
}) => {
  const [methodType, setMethodType] = useState<PaymentMethodType>('ACH');
  const [accountNumber, setAccountNumber] = useState('');
  const [routingNumber, setRoutingNumber] = useState('');
  const [accountType, setAccountType] = useState<'CHECKING' | 'SAVINGS'>('CHECKING');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const oldRouting = returnedMethod?.masked_detail ?? null;
  const oldReason = returnedMethod?.return_reason ?? null;
  const oldReturnCode = returnedMethod?.return_code ?? null;

  const resetAndClose = () => {
    setMethodType('ACH');
    setAccountNumber('');
    setRoutingNumber('');
    setAccountType('CHECKING');
    setError(null);
    onClose();
  };

  const blockedType = getBlockedReplacementType(returnedMethod);

  const validationError = useMemo(
    () => getReplacementValidationError({ methodType, routingNumber, accountNumber, blockedType }),
    [methodType, routingNumber, accountNumber, blockedType]
  );

  const canSubmit =
    canApprove &&
    !submitting &&
    (methodType !== 'ACH' || (!!accountNumber && !!routingNumber)) &&
    !validationError;

  const handleSave = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      // Adding the method resets the supplier to awaiting re-approval (handled
      // server-side); the returned snapshot already reflects the new method and
      // the pending approval state, so no separate approval call is needed.
      const updated = await addSupplierPaymentMethod(supplierId, {
        payment_method: methodType,
        ...(methodType === 'ACH'
          ? {
              bank_routing_number: routingNumber.trim(),
              bank_account_number: accountNumber.trim(),
              bank_account_type: accountType,
            }
          : {}),
        ...(returnedMethod ? { replaced_account_id: returnedMethod.id } : {}),
      });
      toast.success('Payment method saved. Review and approve the supplier to continue.');
      onSuccess(updated);
      resetAndClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update payment method');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && resetAndClose()}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-amber-600" />
            Update Payment Method
          </DialogTitle>
          <DialogDescription>
            {returnedMethod?.review_status === 'NEEDS_REVIEW'
              ? `Replace the returned payment method for ${supplierName}. The new method must differ from the old one.`
              : `Add a replacement payment method for ${supplierName}.`}
          </DialogDescription>
        </DialogHeader>

        {returnedMethod && (
          <div className="rounded-lg border bg-muted/40 p-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Old method (for verification)
            </p>
            <p className="mt-1 text-sm font-medium">
              {returnedMethod.type} · {oldRouting ?? '—'}
            </p>
            {(oldReturnCode || oldReason) && (
              <p className="mt-0.5 text-xs text-amber-700">
                {oldReturnCode ? `ACH return ${oldReturnCode} — ` : ''}
                {oldReason}
                {returnedMethod.returned_at
                  ? `. Disbursement on ${formatDate(returnedMethod.returned_at)} was returned by the bank.`
                  : ''}
              </p>
            )}
          </div>
        )}

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="pm-type">Payment Method Type</Label>
            <Select value={methodType} onValueChange={(v) => setMethodType(v as PaymentMethodType)}>
              <SelectTrigger id="pm-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ACH">ACH</SelectItem>
                <SelectItem value="CHECK">Check</SelectItem>
                <SelectItem value="DIGITAL_WALLET">Digital Wallet</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {methodType === 'ACH' ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="pm-account">Account Number</Label>
                  <Input
                    id="pm-account"
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder="Account number"
                    maxLength={17}
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ''))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="pm-routing">Routing Number</Label>
                  <Input
                    id="pm-routing"
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder="9-digit routing number"
                    maxLength={9}
                    value={routingNumber}
                    onChange={(e) => setRoutingNumber(e.target.value.replace(/\D/g, ''))}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pm-acct-type">Account Type</Label>
                <Select
                  value={accountType}
                  onValueChange={(v) => setAccountType(v as 'CHECKING' | 'SAVINGS')}
                >
                  <SelectTrigger id="pm-acct-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CHECKING">Checking</SelectItem>
                    <SelectItem value="SAVINGS">Savings</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              A {methodType === 'CHECK' ? 'mailed check' : 'digital wallet'} method will be added
              using the address on file. It will be re-verified on save.
            </p>
          )}

          {(validationError || error) && (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-2.5 text-sm text-red-700">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error ?? validationError}</span>
            </div>
          )}

          {!canApprove && (
            <p className="text-xs text-muted-foreground">
              Only client users can save a replacement payment method.
            </p>
          )}
        </div>

        <div className="mt-2 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={resetAndClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSubmit}>
            <ShieldCheck className="mr-2 h-4 w-4" />
            {submitting ? 'Saving…' : 'Save Method'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
